






























































































































































































































import Vue from 'vue'
import './StreamOutputs.scss'
import { Table, TableColumn, Select, Option } from 'element-ui'
import { SocialMediaQuality } from '@/classes/SocialMediaQuality'
import { required, between } from 'vuelidate/lib/validators'
import { StreamUrl } from '@/classes/streamurl'
import { sortBy } from 'lodash'
import { AbilityAction, AbilitySubject } from '@/abilitiesByRole'
import { CustomRtmpDestination, Stream, StreamRate, StreamType } from '@/types/streams'
import { KeyDict } from '@/types'
import { MIXER_DEFAULT_GOP } from '@/types/casts'
import { Feature } from '@/featureFlags/featureFlags'

interface OutputRow {
  streamname: string
  streamurl: string
  streamkey: string
  quality: string
  id: string
  socialmedia?: string
}

interface CastRate {
  fps: string,
  width: number,
  height: number,
  video_bitrate: number,
  adjusted?: boolean,
  interlaced?: boolean,
  gop_size?: number
}

interface ListItem {
  key: string,
  name: string
}

interface NewOutput {
  type: string
  streamurl: string
  streamkey: string
  active: boolean
  streamid?: string
}

interface SplitUrl {
  streamurl?: string
  streamkey?: string
}

const SAME: string = 'same'

const notUpscaling = () => function () {
  //@ts-ignore
  return this.newStreamResolution <= this.castResolution
}

export default Vue.extend({
  name: 'StreamOutputs',
  mounted () {
    this.gop_size = this.castGopSize
  },
  data: () => ({
    newOutput: null as null|NewOutput,
    newframerate: SAME,
    newquality: SAME,
    interlaced: false,
    gop_size: MIXER_DEFAULT_GOP,
    maxNumOfStreams: 3,
    error: ''
  }),
  computed: {
    isSelfServe (): boolean {
      return this.$hasFeature(Feature.SELF_SERVE)
    },
    showAddExisting (): boolean {
      return this.newOutput !== null && this.newOutput.type === 'stream'
    },
    showNewUrl (): boolean {
      return this.newOutput !== null && this.newOutput.type === 'url'
    },
    castRate (): CastRate {
      const rate: CastRate = {
        width: 0,
        height: 0,
        fps: this.castFps.replace('Hz', ''),
        video_bitrate: parseInt(this.castQuality.split('p')[1])
      }
      if (this.castResolution === 720) {
        rate.width = 1280
        rate.height = 720
      }
      else if (this.castResolution === 1080) {
        rate.width = 1920
        rate.height = 1080
      }
      return rate
    },
    teamOutputStreams (): Stream[] {
      let teamoutputs: Stream[] = []
      Object.keys(this.streams).forEach(streamid => {
        const stream = this.streams[streamid]
        if (!stream.deleted && stream.type === 'output' && stream.hasOwnProperty('name')) {
          if (Object.values(this.value).find(output => output.streamname === stream.name) === undefined) {
            teamoutputs.push(stream)
          }
        }
      })
      teamoutputs = sortBy(teamoutputs, [o => o.name.toLowerCase()])
      return teamoutputs
    },
    outputs (): OutputRow[] {
      const streams: OutputRow[] = []
      Object.keys(this.value).forEach(streamkey => {
        const stream = this.value[streamkey]
        const streamname = stream.type === 'stream' ? this.value[streamkey].streamname : 'new'
        const url = this.splitUrl(stream.url)
        const quality = this.qualityToString(stream.rate)
        const data: OutputRow = {
          streamname,
          streamurl: url.streamurl ?? '',
          streamkey: url.streamkey ?? '',
          quality: quality,
          id: streamkey
        }
        if (this.value[streamkey].socialmedia !== undefined) {
          data.socialmedia = this.value[streamkey].socialmedia
        }
        streams.push(data)
      })
      return streams
    },
    nextAvailableKey (): string {
      let index = 1
      while (this.value['output' + index] !== undefined) {
        index += 1
      }
      return 'output' + index
    },
    newRates (): Partial<CastRate> {
      const rates: Partial<CastRate> = {}
      if (this.newframerate !== SAME) {
        rates.fps = this.newframerate
      }
      if (this.newquality !== SAME) {
        if (this.newquality.includes('720p')) {
          rates.width = 1280
          rates.height = 720
        }
        if (this.newquality.includes('1080p')) {
          rates.width = 1920
          rates.height = 1080
        }
        rates.video_bitrate = parseInt(this.newquality.split('p')[1])
      }
      if (this.newquality === SAME && this.gop_size !== MIXER_DEFAULT_GOP) {
        rates.width = this.castRate.width
        rates.height = this.castRate.height
        rates.video_bitrate = this.castRate.video_bitrate
      }
      if (this.newframerate === SAME && this.gop_size !== MIXER_DEFAULT_GOP) {
        rates.fps = this.castRate.fps
      }
      rates.interlaced = this.interlaced
      rates.gop_size = this.gop_size
      return rates
    },
    qualities (): ListItem[] {
      // TODO: Safe to refactor this into instead using entries from state.team.castProperties?
      const qualitylist: ListItem[] = [
        { key: SAME, name: 'Same quality as cast' },

        // now sorted from Lowest to Highest (same as inputs)
        { key: '720p1500', name: '720p 1.5Mbps' },
        { key: '720p2500', name: '720p 2.5Mbps' },
        { key: '720p3000', name: '720p 3.0Mbps' },
        { key: '720p4500', name: '720p 4.5Mbps' },
        { key: '720p6000', name: '720p 6.0Mbps' },
        { key: '720p8000', name: '720p 8.0Mbps' },
        { key: '720p10000', name: '720p 10.0Mbps' },
        { key: '720p12000', name: '720p 12.0Mbps' },
        { key: '720p16000', name: '720p 16.0Mbps' }
      ]

      if (this.castResolution === 1080) {
        qualitylist.push(...[
          { key: '1080p1500', name: '1080p 1.5Mbps' },
          { key: '1080p2500', name: '1080p 2.5Mbps' },
          { key: '1080p3000', name: '1080p 3.0Mbps' },
          { key: '1080p4500', name: '1080p 4.5Mbps' },
          { key: '1080p6000', name: '1080p 6.0Mbps' },
          { key: '1080p8000', name: '1080p 8.0Mbps' },
          { key: '1080p10000', name: '1080p 10.0Mbps' }
        ])
        if (!this.isSelfServe) {
          qualitylist.push(...[
            { key: '1080p12000', name: '1080p 12.0Mbps' },
            { key: '1080p16000', name: '1080p 16.0Mbps' }
          ])
        }
      }
      return qualitylist
    },
    framerates (): ListItem[] {
      const frameratelist: ListItem[] = [
        { key: SAME, name: 'Same fps as cast' },
        { key: '30', name: '@ 30Hz' },
        { key: '29.97', name: '@ 29.97Hz' },
        { key: '25', name: '@ 25Hz' }]
      return frameratelist
    },
    castResolution (): number {
      //Parse out res as an int without leading 0's for comparison, ex., 00720p00600 -> 720
      return +this.castQuality.split('p')[0]
    },
    newStreamResolution (): number {
      const res = this.teamOutputStreams?.find(stream => stream.id === this.newOutput?.streamid)?.resolution?.split('p')[0] ?? ''
      //Parse out res as an int without leading 0's, ex., 00720p00600 -> 720
      return +res
    }
  },
  props: {
    streams: {
      type: Object as () => KeyDict<Stream>
    },
    editMode: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object as () => KeyDict<CustomRtmpDestination>
    },
    castQuality: {
      type: String,
      required: true
    },
    castFps: {
      type: String,
      required: true
    },
    castGopSize: {
      type: Number,
      required: true
    }
  },
  methods: {
    splitUrl (url: string): SplitUrl {
      const strurl = new StreamUrl(url)
      const stream: SplitUrl = {}
      if (strurl.valid) {
        stream.streamurl = strurl.streamurl
        stream.streamkey = strurl.streamkey
      }
      return stream
    },
    qualityTooltip (quality: string): string {
      if (quality === 'Same as cast') {
        const mbps = Math.floor(this.castRate.video_bitrate / 100) / 10
        return `${this.castRate.height}p${this.castRate.fps} ${mbps}Mbps gop:${this.castGopSize}`
      } else {
        return quality
      }
    },
    removeTrailingSlash (url: string): string {
      if (url.endsWith('/')) return url.slice(0, url.length - 1)
      return url
    },
    qualityToString (streamRate: StreamRate | undefined) {
      // An undefined rate means:
      // - Quality is same as cast
      // - Fps is same as cast
      // - Gop is 4 (MIXER_DEFAULT_GOP)
      // - Not interlaced
      let rate = streamRate
      if (rate === undefined) {
        rate = { ...this.castRate }
      }
      if (rate.gop_size === undefined) { rate.gop_size = MIXER_DEFAULT_GOP }
      if (rate.interlaced === undefined) { rate.interlaced = false }

      if (rate.fps === this.castRate.fps && rate.width === this.castRate.width
          && rate.height === this.castRate.height && rate.video_bitrate === this.castRate.video_bitrate
          && rate.gop_size === this.castGopSize && rate.interlaced === false) {
        return 'Same as cast'
      }

      let quality = ''
      if (rate.height !== undefined) {
        quality += rate.height
        if (rate.interlaced) {
          quality += 'i'
        } else {
          quality += 'p'
        }
      }
      if (rate.fps !== undefined) {
        quality += rate.fps
        if (rate.height === undefined) {
          quality += 'fps'
        }
      }
      if (rate.video_bitrate) {
        quality += ` ${rate.video_bitrate / 1000}Mbps`
      }
      quality += ` gop:${rate.gop_size}`

      return quality
    },
    checkUrl (): boolean {
      if (this.newOutput === null) return false
      if (!(this.newOutput.streamurl.startsWith('rtmp://') || this.newOutput.streamurl.startsWith('rtmps://'))) {
        this.error = 'Stream url should start with rtmp:// or rtmps://'
        return false
      }
      const url = this.removeTrailingSlash(this.newOutput.streamurl)
      const args = url.split('/')
      if (args.length !== 4) {
        this.error = 'Stream url should be of the form rtmp://<servername>/<application>'
        return false
      }
      if (args[2].length < 3 || args[3].length < 1) {
        this.error = 'Stream url should be of the form rtmp://<servername>/<application>'
        return false
      }
      if (!this.$can(AbilityAction.Do, AbilitySubject.Ops) && this.newOutput.streamurl.indexOf('kiswe.com') !== -1) {
        this.error = 'Url cannot contain \'kiswe.com\'. This output will not be added.'
        return false
      }
      if (this.newOutput.streamkey.length < 2) {
        this.error = 'Stream Key should be at least 2 characters long'
        return false
      }
      if (this.newframerate !== SAME && this.newquality === SAME)  {
        this.error = 'Cannot update framerate without setting quality'
        return false
      }
      if (this.interlaced && this.newquality === SAME) {
        this.error = 'Cannot enable interlaced without setting quality'
        return false
      }
      return true
    },
    removeOutput (id) {
      const newValues = { ...this.value }
      delete newValues[id]
      this.$emit('input', newValues)
    },
    addURL () {
      if (!this.$v.$invalid && this.checkUrl() && this.newOutput !== null) {
        const newValues = { ...this.value }
        const time = Date.now() / 1000
        const key = this.nextAvailableKey
        newValues[key] = {
          start_time: time,
          streamname: '',
          type: StreamType.Url,
          url: this.removeTrailingSlash(this.newOutput.streamurl) + '/' + this.newOutput.streamkey,
          valid: true,
          active: this.newOutput.active
        }

        if (this.newframerate !== SAME || this.newquality !== SAME || this.gop_size !== MIXER_DEFAULT_GOP || this.interlaced) {
          newValues[key].rate = this.newRates
        }

        const socialmedia = SocialMediaQuality.getSocialMedia(newValues[key].url)
        if (socialmedia) {
          newValues[key].socialmedia = socialmedia
          let newrate = newValues[key].rate !== undefined ? newValues[key].rate! : this.castRate
          if (newrate.fps === undefined) {
            newrate.fps = this.castRate.fps
          }
          newrate = SocialMediaQuality.getMaxQuality(socialmedia, newrate)
          if (newrate.adjusted) {
            newValues[key].rate = newrate
          }
        }
        this.$emit('input', newValues)
        this.newOutput = null
      }
    },
    addStream () {
      if (this.newOutput !== null && this.newOutput.streamid && this.streams[this.newOutput.streamid] !== undefined && !this.$v.newOutput.$invalid) {
        const streamDoc = this.streams[this.newOutput.streamid]
        const stream: CustomRtmpDestination = {
          url: streamDoc.srcurl,
          type: StreamType.Stream,
          streamname: streamDoc.name,
          valid: true,
          start_time: (new Date()).getTime() / 1000,
          active:this.newOutput.active
        }
        if (this.streams[this.newOutput.streamid].rate !== undefined) {
          stream.rate = this.streams[this.newOutput.streamid].rate
        }
        const socialmedia = SocialMediaQuality.getSocialMedia(stream.url)
        if (socialmedia) {
          stream.socialmedia = socialmedia
          let newrate = stream.rate !== undefined ? stream.rate : this.castRate
          if (newrate.fps === undefined) {
            newrate.fps = this.castRate.fps
          }
          newrate = SocialMediaQuality.getMaxQuality(socialmedia, newrate)
          // @ts-ignore
          if (newrate.adjusted) {
            stream.rate = newrate
          }
        }
        const newValues = { ...this.value }
        newValues[this.newOutput.streamid] = stream
        this.$emit('input', newValues)
        this.newOutput = null
      } else if (!this.$v.newOutput.$invalid) {
        this.error = 'Please select a stream'
      }
    },
    addNewOutput (type) {
      this.newOutput = { type: type, streamurl: '', streamkey: '', active: true }
      if (type === 'url') {
        this.newframerate = SAME
        this.newquality = SAME
      }
      this.error = ''
    },
    clearNewStream () {
      this.newOutput = null
    },
    streamname (url: string) {
      const streams = Object.values(this.streams).filter(stream => stream.srcurl === url)
      if (streams[0] !== undefined) {
        return streams[0].name
      }
      return ''
    },
    streamurl (url: string): string|undefined {
      const strurl = new StreamUrl(url)
      return strurl.streamurl
    },
    streamkey (url: string): string|undefined {
      const strurl = new StreamUrl(url)
      return strurl.streamkey
    }
  },
  validations: {
    gop_size: {
      required,
      between: between(1,6)
    },
    newOutput: {
      notUpscaling: notUpscaling()
    }
  },
  components: {
    'el-table': Table,
    'el-table-column': TableColumn,
    'el-select': Select,
    'el-option': Option
  }
})
